/**
 * patchStoreCoordinates — PATCH /api/retailers/stores/{storeId}/coordinates/
 * to update a store's lat/lng (used when the rep manually corrects a
 * miscoded store location). No parent dependency.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, get, put } from '../../utils/indexedDB';
import { BASE_URL, jsonHeaders, classifyResponse, networkErrorResult } from '../httpHelpers';

export interface PatchStoreCoordinatesInput {
  storeId: number;
  latitude: number;
  longitude: number;
}

export interface PatchStoreCoordinatesResponse {
  success?: boolean;
}

interface StoreCoordinatesSingleton {
  key: string;
  byStoreId: Record<string, { latitude: number; longitude: number; updatedAt: string }>;
}

const COORDS_KEY = 'storeCoordinates';

export const patchStoreCoordinatesOp: WriteOp<
  PatchStoreCoordinatesInput,
  PatchStoreCoordinatesInput,
  PatchStoreCoordinatesResponse
> = {
  kind: 'patchStoreCoordinates',

  async applyLocal(input) {
    const existing =
      (await get<StoreCoordinatesSingleton>(STORES.VISIT_SINGLETONS, COORDS_KEY)) ||
      ({ key: COORDS_KEY, byStoreId: {} } as StoreCoordinatesSingleton);
    existing.byStoreId[String(input.storeId)] = {
      latitude: input.latitude,
      longitude: input.longitude,
      updatedAt: new Date().toISOString(),
    };
    await put(STORES.VISIT_SINGLETONS, existing);
  },

  serialize(input, localId) {
    return {
      action: 'patchStoreCoordinates',
      endpoint: `/api/retailers/stores/${input.storeId}/coordinates/`,
      method: 'PATCH',
      payload: input,
      priority: 80,
      maxRetries: 5,
      dependsOn: null,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as PatchStoreCoordinatesInput;
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({ latitude: input.latitude, longitude: input.longitude }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile() {},

  dependsOn() {
    return null;
  },
};

export type PatchStoreCoordinatesOp = typeof patchStoreCoordinatesOp;
